"use client";

import { useEffect } from "react";
import { useSearchParams } from "next/navigation";
import { detectAndStoreDextoolsUtm, sendDextoolsEvent } from "@/lib/dextools-tracking";

/**
 * DextoolsTracker component
 * Detects users arriving from the DexTools widget (utm_source=dextools)
 * and reports a referral_landing event back to the DexTools analytics dashboard.
 * Also records the click in referral_clicks via track-click API.
 */
const LANDING_FIRED_KEY = "dextools_landing_fired";
// Module-level guard: this component is mounted in the root layout, so its
// effect re-runs on every client navigation while the dextools params stay in
// the URL. Without a guard that fired one referral_landing PER navigation —
// ~10 per visit — inflating the admin's "Arrived at FrenFlow" ~10x over the
// real unique visitors GA sees. We report at most once per page load here,
// backed by sessionStorage so reloads within the same tab don't re-fire either.
let landingReported = false;

export default function DextoolsTracker() {
  const searchParams = useSearchParams();

  useEffect(() => {
    if (searchParams?.get("utm_source") !== "dextools") return;

    const utm = detectAndStoreDextoolsUtm();
    if (!utm) return;

    // Fire the landing at most once per session. The module flag covers SPA
    // navigations within one load; sessionStorage covers reloads in the same
    // tab. If storage is unavailable (private mode) we still de-dup the load
    // via the module flag, so the worst case is one landing per reload.
    if (landingReported) return;
    try {
      if (sessionStorage.getItem(LANDING_FIRED_KEY)) {
        landingReported = true;
        return;
      }
      sessionStorage.setItem(LANDING_FIRED_KEY, "1");
    } catch {
      /* private mode — fall through, module flag still de-dups this load */
    }
    landingReported = true;

    // Report to external DexTools webhook
    sendDextoolsEvent({
      type: "referral_landing",
      utmSource: utm.utmSource,
      utmMedium: utm.utmMedium,
      utmCampaign: utm.utmCampaign,
      marketSlug: utm.marketSlug,
      action: "page_view",
    });

    // Also record in referral_clicks so admin dashboard can count it
    fetch("/api/referral/track-click", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        code: "DEXTOOLS",
        landing: window.location.pathname,
      }),
    }).catch(() => { /* fire-and-forget */ });
  }, [searchParams]);

  return null;
}
